import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { ModelLabel } from '@/components/ModelLabel'
import { RedTeamTaskIcon } from '@/components/RedTeamTaskIcon'
import { useBoardData } from '@/components/useBoardData'
import { queryPath, type TaskRunSummary } from '@/lib/task-runs'
import { stageDef } from '@/lib/stages'
import { PageButtons } from './TaskRolloutCards'

export interface RunBoardData { tasks: TaskRunSummary[]; next_offset: number | null; total?: number }
export interface RunBoardFilters { search: string; stage: string; status: string }
const number = (value: number) => value.toLocaleString()

function RowContent({ task }: { task: TaskRunSummary }) {
  const workflow = task.entry_type === 'workflow'
  const running = task.running + (task.other_running ?? 0)
  const queued = task.queued + (task.other_queued ?? 0)
  return <>
    <span className="run-board-identity"><span className={`run-board-icon${workflow ? ' is-workflow' : ''}`}><Icon name={workflow ? 'layers' : 'file'} size={18} /></span>
      <span className="task-board-name"><strong><RedTeamTaskIcon task={task} />{task.title}{task.lifecycle === 'retired' && <small> · Retired</small>}</strong>
        <span className="task-board-subline">{workflow && <small>{number(task.task_count ?? 0)} tasks</small>}<small>{task.program}</small>
          {workflow ? <small>{task.models.length} {task.models.length === 1 ? 'model' : 'models'}</small> : <span className="task-models">{task.models.slice(0, 3).map(model => <ModelLabel key={model} model={model} />)}{task.models.length > 3 && <span title={task.models.slice(3).join(', ')}>+{task.models.length - 3}</span>}</span>}
        </span>
      </span>
    </span>
    <span className="run-board-activity">{running > 0 && <span className="is-running"><i />{number(running)} running</span>}{queued > 0 && <span>{number(queued)} queued</span>}
      {!running && !queued && <span>{task.failed > 0 && task.completed === 0 ? 'Failed' : task.terminated > 0 && task.completed === 0 ? 'Stopped' : 'Finished'}</span>}
    </span>
    <span className="run-board-score" title={`${number(task.scored)} scored model rollouts; weighted by rollout count`}><small>Score</small>{task.mean_score == null ? '—' : task.mean_score.toFixed(2)}</span>
    <span className="run-board-number"><small>Completed</small>{number(task.completed)}</span>
    <span className={`run-board-number${task.failed ? ' has-issue' : ''}`}><small>Failed</small>{task.failed ? number(task.failed) : '—'}</span>
    <span className="run-board-number"><small>Rollouts</small>{number(task.total)}</span>
    <span className="run-board-chevron"><Icon name={workflow ? 'chevron' : 'arrow'} size={15} /></span>
  </>
}

export function RunBoardHeader() {
  return <div className="run-board-row run-board-header" aria-hidden="true"><span>Workflow / task</span><span>Activity</span><span>Mean score</span><span>Completed</span><span>Failed</span><span>Rollouts</span><span /></div>
}

export function TaskBoardRow({ task }: { task: TaskRunSummary }) {
  return <Link className="run-board-row" title={task.display_stage == null ? 'Imported execution history' : stageDef(task.display_stage).label}
    to={`/eng/runs/tasks/${encodeURIComponent(task.task_id)}`}><RowContent task={task} /></Link>
}

function WorkflowTasks({ workflow, filters }: { workflow: TaskRunSummary; filters: RunBoardFilters }) {
  const [offset, setOffset] = useState(0)
  const state = useBoardData<RunBoardData>(queryPath('/eng/runs/task-board', { workflow: workflow.task_id, ...filters, offset, limit: 10 }))
  useEffect(() => { const timer = window.setInterval(() => { if (!document.hidden) void state.reload() }, 10000); return () => clearInterval(timer) }, [state.reload])
  return <div className="workflow-tasks">
    <div className="workflow-tasks-head"><span>Tasks in this workflow</span><Link to={`/eng/tasks/${encodeURIComponent(workflow.task_id)}`}>Workflow <Icon name="arrow" size={12} /></Link></div>
    {state.error && <p className="note err" role="alert">{state.error}</p>}
    {!state.data && !state.error && <p className="muted small">Loading tasks…</p>}
    {state.data?.tasks.map(task => <TaskBoardRow task={task} key={task.task_id} />)}
    {state.data && !state.data.tasks.length && <p className="empty">No matching tasks with model rollout history.</p>}
    {state.data && <PageButtons offset={offset} next={state.data.next_offset} onChange={setOffset} pageSize={10} />}
  </div>
}

export function WorkflowBoardRow({ task, filters, expanded, onToggle }: {
  task: TaskRunSummary; filters: RunBoardFilters; expanded: boolean; onToggle: () => void
}) {
  const id = useId()
  return <div className={`workflow-board-group${expanded ? ' is-expanded' : ''}`}>
    <button className="run-board-row workflow-board-toggle" aria-expanded={expanded} aria-controls={id} onClick={onToggle}>
      <RowContent task={task} />
    </button>
    <div id={id} hidden={!expanded}>{expanded && <WorkflowTasks key={JSON.stringify(filters)} workflow={task} filters={filters} />}</div>
  </div>
}
